import {
  hasEditPermission,
  hasReadPermission,
} from '@/utils/hubPermissions/hasPermissions';
import {
  getAllFeatureKeys,
  getAllModuleKeys,
} from '@/utils/hubPermissions/hubPermissionsConfig';
import { getClientCookieOptions } from '@/utils/auth/cookieSecurity';
import {
  isActivityLogPostBlocked,
  markActivityLogRateLimitedFromError,
} from '@/utils/activityLogging/activityLogClientRateLimit';
import { postHubApi } from '@/utils/hooks/api/typescript/methodHelpers';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';

function normalizeImpersonationOrg(org) {
  if (org == null) return '';
  if (typeof org === 'string') return org.trim();
  if (typeof org === 'object' && org !== null && typeof org.name === 'string') {
    return org.name.trim();
  }
  return String(org).trim();
}

function normalizeImpersonationEmail(email) {
  if (email == null) return '';
  return String(email).trim();
}

/** @param {unknown} err */
function impersonationActivityLogFailureMessage(err) {
  const data = err?.response?.data;
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }
  }
  return 'Could not record this action in the activity log. You can continue using the Hub.';
}

/**
 * Sets the impersonation cookie and records a client activity row (template API).
 * All UI entry points should use this so the activity payload stays correct.
 *
 * @returns {Promise<{ activityLogError: string | null }>}
 */
export async function startImpersonatingUser(org, email) {
  const orgStr = normalizeImpersonationOrg(org);
  const emailStr = normalizeImpersonationEmail(email);

  setCookie(
    'impersonatedUser',
    JSON.stringify({
      email: emailStr,
      org: orgStr,
    }),
    getClientCookieOptions()
  );

  if (isActivityLogPostBlocked()) {
    return { activityLogError: null };
  }

  try {
    await postHubApi('/activities', {
      activityData: {
        module: 'Impersonation',
        status: 'Info',
        feature: '',
        messageTemplateId: 'impersonation_start',
        messageParams: {
          targetEmail: emailStr,
          organization: orgStr,
        },
      },
    });
    return { activityLogError: null };
  } catch (err) {
    markActivityLogRateLimitedFromError(err);
    return { activityLogError: impersonationActivityLogFailureMessage(err) };
  }
}

export function stopImpersonatingUser() {
  deleteCookie('impersonatedUser');
}

export function getImpersonatedUser() {
  return JSON.parse(getCookie('impersonatedUser') || '{}');
}

export function getLeastPermissions(primaryObject, secondaryObject) {
  const leastPermissions = {};

  // Check modules as booleans.
  for (let key of getAllModuleKeys()) {
    leastPermissions[key] = !!(primaryObject[key] && secondaryObject[key]);
  }

  // Check features using off - read - edit system.
  for (let key of getAllFeatureKeys()) {
    if (
      hasEditPermission(primaryObject, key) &&
      hasEditPermission(secondaryObject, key)
    ) {
      leastPermissions[key] = 'edit';
    } else if (
      hasReadPermission(primaryObject, key) &&
      hasReadPermission(secondaryObject, key)
    ) {
      leastPermissions[key] = 'read';
    } else {
      leastPermissions[key] = 'off';
    }
  }

  // We keep any non module/feature keys from the primary object.
  return {
    ...primaryObject,
    ...leastPermissions,
  };
}

export function hasImpersonationPermission(user, org) {
  return (
    hasReadPermission(user, 'administrationUserImpersonation') &&
    hasReadPermission(org, 'administrationUserImpersonation')
  );
}
